import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { X, Plus, Trash2 } from 'lucide-react';
import { normKey, canonKey, normalizePriceItems, priceItemCategory, findPriceMatch } from '../utils/statementItems';

// 단가표 관리 모달 (독립 컴포넌트, 2026-07-30) — 거래처 현황 등 어디서든 페이지 이동 없이 사용
// 2026-08-31 대표 확정: 3구역(보철물/재료/임플란트) → 단일 목록(품목명/단가)로 단순화 (재료 입력 칸도 제거 — 같은 날 대표 지시).
// 저장 스키마·category 추론(priceItemCategory)은 그대로 유지 — 매칭 엔진·실시간 매출 무변경.
// 로드가 끝나기 전에는 표를 보여주지 않는다 (빈 표 저장으로 기존 단가가 날아가는 사고 방지)

const DEFAULT_PROSTHESIS_CHIPS = ['지르코니아 크라운', '이맥스 크라운', 'PFM 크라운', '골드 크라운', '지르코니아 인레이', '골드 인레이'];
const DEFAULT_IMPLANT_CHIPS = ['임플란트'];

const nb = (n) => (n || '').replace(/\s|\(.*?\)/g, '').replace(/치과의원|치과병원|치과$/g, '').toLowerCase();

function PriceListModal({ user, clinicId, clinicName, suggestedItems, onClose, onSaved }) {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [saving, setSaving] = useState(false);
    // 2026-07-30 B안: 미가입 치과는 이름 키(agent_/email_)로 저장 — 같은 치과의 다른 키 문서가 있으면 그 키를 쓴다
    const [effectiveId, setEffectiveId] = useState(clinicId);

    const uid = user?.companyId || auth.currentUser?.uid;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            // 저장된 단가표 — 문서가 아직 없으면 rules가 읽기를 거부함(resource 기준) → 빈 표로 시작
            let saved = [];
            let useId = clinicId;
            try {
                const snap = await getDoc(doc(db, 'priceLists', `${uid}_${clinicId}`));
                saved = snap.exists() ? normalizePriceItems(snap.data().items) : [];
            } catch (_) { /* 미생성 문서 = 처음 등록 */ }
            // 이름 폴백: 같은 치과가 다른 키(agent_↔email_↔실ID)로 저장한 문서 재사용 (키 분열 방지)
            if (!saved.length && nb(clinicName)) {
                try {
                    const { collection, query, where, getDocs } = await import('firebase/firestore');
                    const q0 = await getDocs(query(collection(db, 'priceLists'), where('userId', '==', uid)));
                    const hit = q0.docs.find(d0 => (d0.data().items || []).length && nb(d0.data().partnerName) === nb(clinicName));
                    if (hit) {
                        saved = normalizePriceItems(hit.data().items);
                        // 실 거래처로 열었는데 이름 키만 있으면 실 키로 승계(저장 시 실 키에 기록)
                        if (!(String(clinicId).startsWith('agent_') || String(clinicId).startsWith('email_'))) useId = clinicId;
                        else useId = hit.data().partnerId || clinicId;
                    }
                } catch (_) { /* 폴백 실패 시 새 키로 진행 */ }
            }
            setEffectiveId(useId);
            const list = saved.map(p => ({ ...p }));
            const existingKeys = new Set(list.map(p => normKey(p.serviceName)));

            // 치과 단축어 프리셋을 미기입 행으로 자동 나열 (단가만 채우면 됨)
            try {
                const u = await getDoc(doc(db, 'users', clinicId));
                const d = u.exists() ? u.data() : {};
                const pros = new Set(DEFAULT_PROSTHESIS_CHIPS);
                (d.customProsthesisOptions || []).forEach(opt => {
                    const label = typeof opt === 'string' ? opt : (opt.label || opt.value || '');
                    if (label) pros.add(label);
                });
                const imps = new Set(DEFAULT_IMPLANT_CHIPS);
                [...(d.savedImplantOptions || []), ...(d.customImplantOptions || [])].forEach(opt => {
                    const label = typeof opt === 'string' ? opt : (opt.label || opt.value || '');
                    if (label) imps.add(label);
                });
                pros.forEach(name => {
                    if (!existingKeys.has(normKey(name))) {
                        existingKeys.add(normKey(name));
                        list.push({ serviceName: name, unitPrice: 0, material: '', source: 'clinic_preset', category: 'prosthesis' });
                    }
                });
                imps.forEach(name => {
                    if (!existingKeys.has(normKey(name))) {
                        existingKeys.add(normKey(name));
                        list.push({ serviceName: name, unitPrice: 0, material: '', source: 'clinic_preset', category: 'implant' });
                    }
                });
                // 재료 입력 칸 제거(2026-08-31)로 customMaterialMap 자동완성도 사용 안 함
            } catch (_) { /* 프리셋 없이 진행 */ }

            // 미매칭 자동 제안 — 호출 화면(명세서 작성)이 넘긴 이번 명세서 품목 중 단가표에 없는 것
            (suggestedItems || []).forEach(it => {
                const sn = (it.serviceName || '').trim();
                if (!sn) return;
                // 폴백 매칭(재료 단독·부분포함)으로 이미 단가가 잡히는 품목은 제안 제외 (2026-09-01 검증 P2 — 과잉 제안 노이즈)
                try {
                    const m = saved.length ? findPriceMatch(saved, null, sn, it.material) : null;
                    if (m && (m.unitPrice || 0) > 0) return;   // 0원 매칭이면 제안 유지 (단가를 채우게)
                } catch (_) { /* 제안은 계속 */ }
                const cat = priceItemCategory({ serviceName: sn });
                const dup = list.some(pRow => normKey(pRow.serviceName) === normKey(sn)
                    || (priceItemCategory(pRow) === cat && canonKey(pRow.serviceName) === canonKey(sn)));
                if (dup) return;
                list.push({ serviceName: sn, unitPrice: 0, material: it.material || '', source: 'from_items', category: cat });
            });

            setRows(list.length > 0 ? list : [{ serviceName: '', unitPrice: 0, material: '', source: 'manual', category: 'prosthesis' }]);
        } catch (e) {
            console.error('단가표 로드 실패:', e);
            alert('단가표를 불러오지 못했습니다. 다시 열어주세요.');
            onClose();
            return;
        }
        setLoading(false);
    }, [uid, clinicId, onClose]);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        if (saving) return;
        setSaving(true);
        try {
            const validItems = rows
                .filter(item => (item.serviceName || '').trim())
                .filter(item => !((item.source === 'clinic_preset' || item.source === 'from_items') && !(item.unitPrice > 0)))
                .map(item => ({
                    serviceName: item.serviceName,
                    unitPrice: item.unitPrice || 0,
                    material: item.material || '',
                    source: item.source === 'from_items' ? 'manual' : (item.source || 'manual'),
                    category: priceItemCategory(item),
                    name: item.serviceName,
                    price: item.unitPrice || 0,
                }));
            await setDoc(doc(db, 'priceLists', `${uid}_${effectiveId}`), {
                userId: uid,
                partnerId: effectiveId,
                partnerName: clinicName || '',
                items: validItems,
                updatedAt: Timestamp.now(),
            });
            if (onSaved) onSaved(validItems);
            onClose();
            alert('단가표가 저장되었습니다.');
        } catch (e) {
            console.error('단가표 저장 실패:', e);
            alert('저장에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const updateRow = (idx, field, value) => {
        const updated = [...rows];
        updated[idx] = { ...updated[idx], [field]: value };
        setRows(updated);
    };

    // 단일 목록 (2026-08-31 대표 확정): 보철물/재료/임플란트 3구역 → 품목 하나의 목록.
    // 저장 스키마(serviceName/unitPrice/material)와 category 추론(priceItemCategory)은 그대로 —
    // 구버전 3구역 입력분도 한 목록으로 표시하고, 분류는 회색 소배지로만 참고 표시한다.
    const CATEGORY_BADGE = { material: '재료', implant: '임플란트' };   // 보철물(기본)은 배지 생략
    const unpricedCount = rows.filter(i => (i.serviceName || '').trim() && !(i.unitPrice > 0)).length;

    return (
        <div style={S.overlay}>
            <div style={S.modal}>
                <div style={S.header}>
                    <h3 style={S.title}>단가표 관리 <span style={S.subtitle}>{clinicName}</span></h3>
                    <button onClick={onClose} style={S.closeBtn}><X size={20} /></button>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: 50, color: '#94A3B8', fontSize: 14 }}>단가표 불러오는 중...</div>
                ) : (
                    <>
                        {unpricedCount > 0 && (
                            <div style={S.warn}>단가 미입력 <b>{unpricedCount}건</b> — 미입력 항목은 저장 시 제외되며 자동 적용되지 않습니다.</div>
                        )}
                        <div style={{ marginBottom: 14 }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '2px 0 4px' }}>
                                <span style={{ fontSize: 13.5, fontWeight: 800, color: '#4f46e5' }}>품목</span>
                                <span style={{ fontSize: 11.5, color: '#94a3b8' }}>치과 단축어 그대로 (예: 지르크라운 · 임플란트 · 지르코니아 — 재료 단독 단가도 한 행으로)</span>
                            </div>
                            <table style={S.table}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid #E2E8F0' }}>
                                        <th style={S.th}>품목명 (치과 단축어)</th>
                                        <th style={{ ...S.th, width: 120, textAlign: 'right' }}>단가 (원)</th>
                                        <th style={{ padding: 8, width: 40 }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 && (
                                        <tr><td colSpan={3} style={{ padding: 8, fontSize: 12.5, color: '#cbd5e1' }}>등록된 항목 없음</td></tr>
                                    )}
                                    {rows.map((item, idx) => {
                                        const unpriced = !(item.unitPrice > 0);
                                        const badge = CATEGORY_BADGE[priceItemCategory(item)];   // 구버전 3구역 데이터 참고 표시
                                        return (
                                            <tr key={idx} style={{ borderBottom: '1px solid #F1F5F9', background: unpriced ? '#F8FAFC' : 'transparent', opacity: unpriced ? 0.75 : 1 }}>
                                                <td style={S.td}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                        <input value={item.serviceName}
                                                            onChange={(e) => updateRow(idx, 'serviceName', e.target.value)}
                                                            placeholder={'예: 지르코니아 크라운'}
                                                            style={S.input} />
                                                        {badge && (
                                                            <span title="자동 분류 (참고 표시 — 매칭에는 영향 없음)" style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 700, color: '#64748B', background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: 6, padding: '2px 6px' }}>{badge}</span>
                                                        )}
                                                        {/* 재료 입력 칸 제거 (2026-08-31 대표 지시 "품목명이랑 단가만") — 저장된 material 값은
                                                            보존(재료별 단가 매칭 불변), 같은 품목명이 재료로 나뉜 구데이터 구분용 회색 참고 표시만 */}
                                                        {(item.material || '').trim() && (
                                                            <span title="재료 구분 (이전에 입력된 값 — 참고 표시)" style={{ flexShrink: 0, fontSize: 11, color: '#94A3B8' }}>· {item.material}</span>
                                                        )}
                                                        {item.source === 'from_items' && (
                                                            <span title="이번 명세서 항목 중 단가표에 없어 제안된 품목" style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 700, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '2px 6px' }}>발견</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td style={S.td}>
                                                    <input type="number" value={item.unitPrice}
                                                        onChange={(e) => updateRow(idx, 'unitPrice', Number(e.target.value) || 0)}
                                                        style={{ ...S.input, textAlign: 'right' }} />
                                                </td>
                                                <td style={{ padding: '6px 4px' }}>
                                                    <button onClick={() => setRows(rows.filter((_, i) => i !== idx))}
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: 4 }}>
                                                        <Trash2 size={16} />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            <button onClick={() => setRows([...rows, { serviceName: '', unitPrice: 0, material: '', source: 'manual' }])}
                                style={S.addBtn}>
                                <Plus size={14} /> 품목 추가
                            </button>
                        </div>
                        <div style={S.actions}>
                            <button onClick={onClose} style={S.cancelBtn}>취소</button>
                            <button onClick={save} disabled={saving} style={{ ...S.saveBtn, opacity: saving ? 0.6 : 1 }}>
                                {saving ? '저장 중...' : '저장'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

const S = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
    modal: { width: '100%', maxWidth: 680, maxHeight: '86vh', overflowY: 'auto', background: '#fff', borderRadius: 16, padding: '22px 24px' },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
    title: { margin: 0, fontSize: 18, fontWeight: 800, color: '#0F172A' },
    subtitle: { marginLeft: 8, fontSize: 13.5, fontWeight: 600, color: '#64748B' },
    closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', padding: 4 },
    warn: { marginBottom: 8, fontSize: 12.5, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '7px 10px' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { padding: 8, textAlign: 'left', color: '#64748B', fontSize: 13 },
    td: { padding: '6px 8px' },
    input: { width: '100%', boxSizing: 'border-box', padding: '7px 9px', fontSize: 13.5, border: '1px solid #E2E8F0', borderRadius: 8, outline: 'none' },
    addBtn: { display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6, padding: '6px 10px', fontSize: 12.5, fontWeight: 700, color: '#4F46E5', background: '#EEF2FF', border: '1px dashed #C7D2FE', borderRadius: 8, cursor: 'pointer' },
    actions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 },
    cancelBtn: { padding: '10px 18px', fontSize: 13.5, fontWeight: 700, color: '#475569', background: '#F1F5F9', border: 'none', borderRadius: 10, cursor: 'pointer' },
    saveBtn: { padding: '10px 22px', fontSize: 13.5, fontWeight: 700, color: '#fff', background: '#4F46E5', border: 'none', borderRadius: 10, cursor: 'pointer' },
};

export default PriceListModal;
